import type { VuetifyOptions } from 'vuetify'

import { resolveVuetifyTheme } from '@core/utils/vuetify'
import { themeConfig } from '@themeConfig'

export const staticPrimaryColor = '#3F006B'

const theme: VuetifyOptions['theme'] = {
  defaultTheme: resolveVuetifyTheme(),
  themes: {
    light: {
      dark: false,
      colors: {
        'primary': staticPrimaryColor,
        'secondary': '#222E50',
        'on-secondary': '#fff',

        // The "Insurance Eligibility Verification Flow" Figma file's --secondary design
        // token (a purple accent) — distinct from Vuetify's own `secondary` (navy) above,
        // which has no relation to that Figma file. Use this, not `secondary`, when
        // matching that design (e.g. RecoveryPatientBanner.vue's avatar).
        'figma-accent': '#BA92CC',
        'on-figma-accent': '#fff',
        'success': '#00a367',
        'info': '#1e6cfc',

        // Darkened from the original mustard '#fdcb35' — Vuetify's "tonal" variant
        // (VAlert, VChip, etc.) renders text in this exact color over a pale tint
        // of itself, and the original shade was low-contrast/hard to read there.
        'warning': '#8a5a00',
        'error': '#e03409',
        'on-primary': '#FFFFFF',
        'on-success': '#FFFFFF',
        'on-warning': '#FFFFFF',
        'background': '#F4F5FA',
        'on-background': '#3A3541',
        'on-surface': '#3A3541',
        'grey-50': '#FAFAFA',
        'grey-100': '#F0F2F8',
        'grey-200': '#EEEEEE',
        'grey-300': '#E0E0E0',
        'grey-400': '#BDBDBD',
        'grey-500': '#9E9E9E',
        'grey-600': '#757575',
        'grey-700': '#616161',
        'grey-800': '#424242',
        'grey-900': '#212121',

        // Material's `outline` role (Vuetify doesn't ship it). Several components already
        // reference rgb(var(--v-theme-outline)) for borders; without this they render none.
        'outline': '#DBDADE',
        'perfect-scrollbar-thumb': '#DBDADE',
        'skin-bordered-background': '#fff',
        'skin-bordered-surface': '#fff',
      },

      variables: {
        'code-color': '#d400ff',
        'overlay-scrim-background': '#3A3541',
        'overlay-scrim-opacity': 0.5,
        'hover-opacity': 0.04,
        'focus-opacity': 0.1,
        'selected-opacity': 0.12,
        'activated-opacity': 0.1,
        'pressed-opacity': 0.14,
        'dragged-opacity': 0.1,
        'disabled-opacity': 0.68,
        'border-color': '#3A3541',
        'table-header-background': '#F9FAFC',
        'custom-background': '#F9F8F9',

        // Shadows
        'shadow-key-umbra-opacity': 'rgba(var(--v-theme-on-surface), 0.08)',
        'shadow-key-penumbra-opacity': 'rgba(var(--v-theme-on-surface), 0.12)',
        'shadow-key-ambient-opacity': 'rgba(var(--v-theme-on-surface), 0.04)',
      },
    },
    dark: {
      dark: true,
      colors: {
        'primary':
          localStorage.getItem(`${themeConfig.app.title}-darkThemePrimaryColor`)
          || staticPrimaryColor,
        'secondary': '#8A8D93',
        'on-secondary': '#fff',
        'figma-accent': '#BA92CC',
        'on-figma-accent': '#fff',
        'success': '#00a367',
        'info': '#1e6cfc',

        // Darkened from the original mustard '#fdcb35' — Vuetify's "tonal" variant
        // (VAlert, VChip, etc.) renders text in this exact color over a pale tint
        // of itself, and the original shade was low-contrast/hard to read there.
        'warning': '#8a5a00',
        'error': '#e03409',
        'on-primary': '#FFFFFF',
        'on-success': '#FFFFFF',
        'on-warning': '#FFFFFF',
        'background': '#333333',
        'on-background': '#E7E3FC',
        'surface': '#222222',
        'on-surface': '#E7E3FC',
        'grey-50': '#2A2E42',
        'grey-100': '#474360',
        'grey-200': '#4A5072',
        'grey-300': '#5E6692',
        'grey-400': '#7983BB',
        'grey-500': '#8692D0',
        'grey-600': '#AAB3DE',
        'grey-700': '#B6BEE3',
        'grey-800': '#CFD3EC',
        'grey-900': '#E7E9F6',
        'outline': '#4A5072',
        'perfect-scrollbar-thumb': '#4A5072',
        'skin-bordered-background': '#312d4b',
        'skin-bordered-surface': '#312d4b',
      },
      variables: {
        'code-color': '#d400ff',
        'overlay-scrim-background': '#2C2942',
        'overlay-scrim-opacity': 0.6,
        'hover-opacity': 0.04,
        'focus-opacity': 0.1,
        'selected-opacity': 0.12,
        'activated-opacity': 0.1,
        'pressed-opacity': 0.14,
        'dragged-opacity': 0.1,
        'disabled-opacity': 0.68,
        'border-color': '#E7E3FC',
        'table-header-background': '#3D3759',
        'custom-background': '#373452',

        // Shadows
        'shadow-key-umbra-opacity': 'rgba(20, 18, 33, 0.08)',
        'shadow-key-penumbra-opacity': 'rgba(20, 18, 33, 0.12)',
        'shadow-key-ambient-opacity': 'rgba(20, 18, 33, 0.04)',
      },
    },
  },
}

export default theme
